import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export const FONT_FAMILIES = {
  display: "'PP Editorial Old', 'Cormorant Garamond', Georgia, serif",
  sans: "'Inter', system-ui, sans-serif",
  mono: "'JetBrains Mono', ui-monospace, monospace",
  script: "'Dancing Script', 'Brush Script MT', cursive",
  slab: "'Roboto Slab', 'Rockwell', serif",
  condensed: "'Oswald', 'Arial Narrow', sans-serif",
};

const FONT_FAMILY_OPTIONS = [
  { value: "display", label: "Display serif" },
  { value: "sans", label: "Sans" },
  { value: "mono", label: "Mono" },
  { value: "script", label: "Script" },
  { value: "slab", label: "Slab serif" },
  { value: "condensed", label: "Condensed" },
];

const FONT_WEIGHTS = [
  { value: 300, label: "Light" },
  { value: 400, label: "Regular" },
  { value: 500, label: "Medium" },
  { value: 600, label: "Semibold" },
  { value: 700, label: "Bold" },
  { value: 800, label: "Black" },
];

const TEXT_PRESET_COLORS = [
  "#1c1b17",
  "#FFFFFF",
  "#C27A62",
  "#8A9A86",
  "#806244",
  "#525252",
  "#E11D48",
  "#0EA5E9",
];

export default function MoodboardTextStyleDialog({
  open,
  onOpenChange,
  item,
  onChange,
}) {
  if (!item || item.kind !== "text") return null;

  function set(patch) {
    onChange(patch);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl"
        data-testid="text-style-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            Text style
          </DialogTitle>
          <DialogDescription className="text-sm text-stone-500">
            Adjust typography for the selected text block.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          {/* Content */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
              Content
            </label>
            <textarea
              value={item.text || ""}
              onChange={(e) => set({ text: e.target.value })}
              rows={3}
              placeholder="Type something…"
              data-testid="text-edit-textarea"
              className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-stone-900 resize-none"
              style={{
                fontFamily:
                  FONT_FAMILIES[item.font_family] || FONT_FAMILIES.display,
                fontWeight: item.font_weight || 400,
                fontStyle: item.font_style || "normal",
                textDecoration: item.text_decoration || "none",
                textTransform: item.text_transform || "none",
              }}
            />
          </div>

          {/* Font family + weight */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
                Family
              </label>
              <select
                value={item.font_family || "display"}
                onChange={(e) => set({ font_family: e.target.value })}
                data-testid="text-font-select"
                className="w-full h-10 px-3 rounded-lg bg-white text-stone-900 border border-stone-200 text-sm"
                style={{
                  fontFamily:
                    FONT_FAMILIES[item.font_family] || FONT_FAMILIES.display,
                }}
              >
                {FONT_FAMILY_OPTIONS.map((f) => (
                  <option
                    key={f.value}
                    value={f.value}
                    style={{ fontFamily: FONT_FAMILIES[f.value] }}
                  >
                    {f.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
                Weight
              </label>
              <select
                value={item.font_weight || 400}
                onChange={(e) =>
                  set({ font_weight: Number(e.target.value) })
                }
                data-testid="text-weight-select"
                className="w-full h-10 px-3 rounded-lg bg-white text-stone-900 border border-stone-200 text-sm"
              >
                {FONT_WEIGHTS.map((w) => (
                  <option
                    key={w.value}
                    value={w.value}
                    style={{ fontWeight: w.value }}
                  >
                    {w.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Size + transform */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
                Size
              </label>
              <Input
                type="number"
                min={8}
                max={400}
                value={item.font_size || 32}
                onChange={(e) =>
                  set({ font_size: Number(e.target.value) || 32 })
                }
                data-testid="text-size-input"
                className="h-10"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
                Transform
              </label>
              <select
                value={item.text_transform || "none"}
                onChange={(e) => set({ text_transform: e.target.value })}
                data-testid="text-transform-select"
                className="w-full h-10 px-3 rounded-lg bg-white text-stone-900 border border-stone-200 text-sm"
              >
                <option value="none">Aa Normal</option>
                <option value="uppercase">AA Uppercase</option>
                <option value="lowercase">aa Lowercase</option>
                <option value="capitalize">Aa Capitalize</option>
              </select>
            </div>
          </div>

          {/* Line height + letter spacing */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
                Line height
              </label>
              <Input
                type="number"
                step="0.05"
                min="0.8"
                max="3"
                value={
                  item.line_height !== undefined ? item.line_height : 1.15
                }
                onChange={(e) =>
                  set({ line_height: Number(e.target.value) || 1.15 })
                }
                data-testid="text-lineheight-input"
                className="h-10"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
                Letter spacing (em)
              </label>
              <Input
                type="number"
                step="0.01"
                min="-0.2"
                max="0.6"
                value={item.letter_spacing || 0}
                onChange={(e) =>
                  set({ letter_spacing: Number(e.target.value) || 0 })
                }
                data-testid="text-letterspacing-input"
                className="h-10"
              />
            </div>
          </div>

          {/* Style toggles + alignment */}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
                Style
              </label>
              <div
                className="flex items-center bg-stone-100 rounded-full p-0.5 gap-0.5"
                data-testid="text-format-group"
              >
                <button
                  type="button"
                  onClick={() =>
                    set({
                      font_style:
                        (item.font_style || "normal") === "italic"
                          ? "normal"
                          : "italic",
                    })
                  }
                  data-testid="text-italic-toggle"
                  title="Italic"
                  className={`h-8 w-9 rounded-full text-sm italic transition-colors ${
                    item.font_style === "italic"
                      ? "bg-stone-900 text-white"
                      : "text-stone-700 hover:bg-stone-200"
                  }`}
                >
                  I
                </button>
                <button
                  type="button"
                  onClick={() =>
                    set({
                      text_decoration:
                        (item.text_decoration || "none") === "underline"
                          ? "none"
                          : "underline",
                    })
                  }
                  data-testid="text-underline-toggle"
                  title="Underline"
                  className={`h-8 w-9 rounded-full text-sm underline transition-colors ${
                    item.text_decoration === "underline"
                      ? "bg-stone-900 text-white"
                      : "text-stone-700 hover:bg-stone-200"
                  }`}
                >
                  U
                </button>
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
                Align
              </label>
              <div
                className="flex items-center bg-stone-100 rounded-full p-0.5"
                data-testid="text-align-group"
              >
                {[
                  { v: "left", label: "L" },
                  { v: "center", label: "C" },
                  { v: "right", label: "R" },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => set({ align: opt.v })}
                    data-testid={`text-align-${opt.v}`}
                    title={`Align ${opt.v}`}
                    className={`h-8 w-9 rounded-full text-xs font-semibold transition-colors ${
                      (item.align || "left") === opt.v
                        ? "bg-stone-900 text-white"
                        : "text-stone-600 hover:bg-stone-200"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Color */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
              Color
            </label>
            <div className="flex items-center gap-2 flex-wrap">
              {TEXT_PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => set({ color: c })}
                  data-testid={`text-color-preset-${c.replace("#", "")}`}
                  className={`w-7 h-7 rounded-full border transition-transform ${
                    (item.color || "#1c1b17") === c
                      ? "ring-2 ring-stone-900 scale-110"
                      : "border-stone-200 hover:scale-105"
                  }`}
                  style={{ background: c }}
                  aria-label={`color ${c}`}
                />
              ))}
              <label
                className="flex items-center gap-1.5 bg-stone-100 h-8 px-2.5 rounded-full cursor-pointer"
                title="Custom colour"
              >
                <span className="text-[10px] uppercase tracking-[0.15em] text-stone-500">
                  Custom
                </span>
                <input
                  type="color"
                  value={item.color || "#1c1b17"}
                  onChange={(e) => set({ color: e.target.value })}
                  data-testid="text-color-input"
                  className="w-6 h-6 rounded-full border-none bg-transparent cursor-pointer p-0"
                />
              </label>
            </div>
          </div>

          {/* Live preview */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
              Preview
            </label>
            <div
              className="rounded-lg border border-stone-200 bg-stone-50 px-4 py-5 break-words"
              data-testid="text-style-preview"
              style={{
                fontFamily:
                  FONT_FAMILIES[item.font_family] || FONT_FAMILIES.display,
                fontSize: `${Math.min(item.font_size || 32, 56) * 0.6}px`,
                fontWeight: item.font_weight || 400,
                fontStyle: item.font_style || "normal",
                textDecoration: item.text_decoration || "none",
                textTransform: item.text_transform || "none",
                lineHeight:
                  item.line_height !== undefined ? item.line_height : 1.15,
                letterSpacing: `${item.letter_spacing || 0}em`,
                color: item.color || "#1c1b17",
                textAlign: item.align || "left",
              }}
            >
              {item.text || "Type something…"}
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <Button
            onClick={() => onOpenChange(false)}
            data-testid="text-style-done"
            className="rounded-full bg-stone-900 text-white hover:bg-stone-800"
          >
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
